/* eslint-disable */
export type ImageInfo = {
  width: number;
  height: number;
  aspect: number; // width / height
  ratio: string; // e.g., "16:9"
  orientation: "landscape" | "portrait" | "square";
};

export async function getImageInfo(file: File): Promise<ImageInfo> {
  if (!(file instanceof File) || !file.type.startsWith("image/")) {
    throw new Error("getImageInfo: expected an image File");
  }

  // 1) Fast path: createImageBitmap (no DOM; respects EXIF via imageOrientation)
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" as any });
    const info = formatInfo(bmp.width, bmp.height);
    bmp.close?.();
    return info;
  } catch {
    // fall through to <img>.decode()
  }

  // 2) Fallback: blob URL + HTMLImageElement.decode()
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode(); // wait until decoded
    return formatInfo(img.naturalWidth, img.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function formatInfo(w: number, h: number): ImageInfo {
  if (!w || !h) throw new Error("Invalid intrinsic image size");
  const aspect = w / h;

  // gcd to reduce ratio (iterative to keep TS happy)
  const gcd = (a: number, b: number) => {
    while (b) [a, b] = [b, a % b];
    return a || 1;
  };
  const d = gcd(w, h);
  const ratio = `${Math.round(w / d)}:${Math.round(h / d)}`;

  const orientation: ImageInfo["orientation"] =
      w === h ? "square" : w > h ? "landscape" : "portrait";

  return { width: w, height: h, aspect, ratio, orientation };
}
